import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { TrendIndicator } from './TrendIndicator'
import type { TrendDirection } from '@/types'

interface StepsData {
  date: string
  value: number
}

interface StepsChartProps {
  data: StepsData[]
  goal?: number
  trend?: {
    direction: TrendDirection
    changePercent?: number
  }
  isLoading?: boolean
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatShortDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function ChartSkeleton() {
  return (
    <div className="hearth-card p-5">
      <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
      <div className="mt-4 h-64 bg-gray-100 rounded animate-pulse" />
    </div>
  )
}

export function StepsChart({ data, goal = 10000, trend, isLoading }: StepsChartProps) {
  if (isLoading) {
    return <ChartSkeleton />
  }

  const average = data.length > 0
    ? Math.round(data.reduce((sum, d) => sum + d.value, 0) / data.length)
    : 0

  return (
    <div className="hearth-card p-5">
      <div className="mb-4 flex flex-row items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">Steps</h3>
          <p className="mt-0.5 text-[13px] text-gray-500">
            Average <span className="font-rounded font-medium tabular-nums">{average.toLocaleString()}</span> steps a day
          </p>
        </div>
        {trend && (
          <TrendIndicator
            trend={trend.direction}
            changePercent={trend.changePercent}
            showLabel
            size="sm"
          />
        )}
      </div>
      <div>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-gray-200)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatShortDate}
                tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                axisLine={{ stroke: 'var(--color-gray-200)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload as StepsData
                    return (
                      <div className="rounded-lg border border-gray-200 bg-surface px-3 py-2 shadow-lg">
                        <p className="text-xs text-gray-500">{formatDate(data.date)}</p>
                        <p className="font-rounded text-sm font-semibold tabular-nums text-gray-900">
                          {data.value.toLocaleString()} steps
                        </p>
                        {goal && (
                          <p className="text-xs text-gray-500">
                            {data.value >= goal ? 'Goal reached!' : `${Math.round((data.value / goal) * 100)}% of goal`}
                          </p>
                        )}
                      </div>
                    )
                  }
                  return null
                }}
              />
              {goal && (
                <ReferenceLine
                  y={goal}
                  stroke="#52A67A"
                  strokeDasharray="5 5"
                  label={{
                    value: 'Goal',
                    position: 'right',
                    fill: '#52A67A',
                    fontSize: 12,
                  }}
                />
              )}
              <Bar
                dataKey="value"
                fill="#93C8AB"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-64 items-center justify-center">
            <p className="text-sm text-gray-500">Step readings appear here as they sync.</p>
          </div>
        )}
      </div>
    </div>
  )
}
